<template>
  <section :aria-label="listTitle" class="ai-menu-v2__recent" :class="{ 'is-mobile': mobile }">
    <button
      :aria-expanded="expanded"
      class="ai-menu-v2__recent-title"
      type="button"
      @click="toggleExpanded"
    >
      {{ listTitle }}
      <i
        aria-hidden="true"
        class="t-iconfont"
        :class="expanded ? 'icon-arrow-down-s-line' : 'icon-arrow-right-s-line'"
      ></i>
    </button>
    <div v-show="expanded" class="ai-menu-v2__recent-list">
      <SessionItem
        v-for="session in recentSessions"
        :key="session.sessionId"
        :active="appStore.aiSalesMentorSession?.sessionId === session.sessionId"
        :busy="actionBusy"
        :is-top="isTop"
        :long-press-disabled="mobileMenuVisible"
        :mobile="mobile"
        :session="session"
        @command="handleCommand"
        @longpress="handleLongPress"
        @select="handleClickSession"
      />
      <div v-if="!recentSessions.length" class="ai-menu-v2__session-status">
        {{ isTop ? '暂无置顶任务' : '暂无会话' }}
      </div>
    </div>
    <MobileSessionActions
      v-if="mobile"
      v-model="mobileMenuVisible"
      :anchor-rect="mobileAnchorRect"
      :is-top="isTop"
      :session="mobileMenuSession"
      @command="handleCommand"
    />
    <MobileDeleteDialog
      v-if="mobile && dialogType === 'delete'"
      v-model:show="dialogVisible"
      :loading="actionBusy"
      message="删除后，任务中的对话内容将彻底删除，确定删除吗"
      title="删除任务"
      @confirm="confirmDialog('')"
    />
    <MobileRenameDialog
      v-else-if="mobile"
      v-model:show="dialogVisible"
      :loading="actionBusy"
      :name="dialogSession?.title || ''"
      @confirm="confirmDialog"
    />
    <SessionActionDialog
      v-else
      v-model="dialogVisible"
      :busy="actionBusy"
      :session="dialogSession"
      :type="dialogType"
      @confirm="confirmDialog"
    />
  </section>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import {
  updateSession,
  updateSessionTop,
  renameSession,
  deleteSession
} from '@/api/new-agent/qca.js'
import { useAppStore } from '@/store/useAppStore'
import emitter from '@/utils/mitt'
import MobileDeleteDialog from '@/components/ai-mobile/delete-dialog.vue'
import MobileRenameDialog from '@/components/ai-mobile/rename-dialog.vue'
import { useSessionList } from './use-session-list'
import SessionItem from './recent-conversations/session-item.vue'
import MobileSessionActions from './recent-conversations/mobile-session-actions.vue'
import SessionActionDialog from './recent-conversations/session-action-dialog.vue'

defineOptions({ name: 'RecentConversations' })
const props = defineProps({ mobile: Boolean, isTop: { type: Number, default: 0 } })
const emits = defineEmits(['handleOpenAiAgent', 'navigate', 'updated', 'selected', 'topChanged'])
const appStore = useAppStore()
const route = useRoute()
const listTitle = computed(() => (props.isTop ? '置顶' : '最近任务'))
const expanded = ref(true)
const actionBusy = ref(false)
const dialogVisible = ref(false)
const dialogType = ref('rename')
const dialogSession = ref(null)
const mobileMenuVisible = ref(false)
const mobileMenuSession = ref(null)
const mobileAnchorRect = ref(null)
let isUnmounted = false
let pageActiveTimer = null
let desktopEventsRegistered = false

const syncCurrentSessionTitle = (sessions) => {
  const currentSession = appStore.aiSalesMentorSession
  if (!currentSession?.sessionId) return
  const matched = sessions.find((session) => session.sessionId === currentSession.sessionId)
  if (matched?.title && matched.title !== currentSession.title) {
    appStore.setAiSalesMentorSession({ ...currentSession, ...matched })
  }
}

const {
  recentSessions,
  sessionsLoading,
  sessionsFinished,
  loadSessions,
  refreshFirstPageSessions,
  loadMore,
  removeLocalSession,
  waitForPendingRequests
} = useSessionList({
  getIsTop: () => props.isTop,
  onLoaded: syncCurrentSessionTitle,
  onUpdated: () => emits('updated')
})
const handleLoadMore = () => {
  if (actionBusy.value || !expanded.value) return
  return loadMore()
}

const toggleExpanded = async () => {
  expanded.value = !expanded.value
  await nextTick()
  emits('updated')
}

const handleLongPress = (session, rect) => {
  mobileMenuSession.value = session
  mobileAnchorRect.value = rect
  mobileMenuVisible.value = true
  if (!window.uni) return
  window.uni.postMessage({ data: { type: 'startVibrate' } })
}

const handleCommand = async (command, session) => {
  if (actionBusy.value) return
  if (command !== 'top') {
    dialogSession.value = session
    dialogType.value = command
    dialogVisible.value = true
    return
  }
  actionBusy.value = true
  try {
    const isTop = props.isTop ? 0 : 1
    await updateSessionTop({ id: session.sessionId, isTop })
    // 等待本列表已有请求完成，再移除当前行，避免旧响应把它重新加回来。
    await waitForPendingRequests()
    if (isUnmounted) return
    removeLocalSession(session.sessionId)
    emits('topChanged', isTop)
  } catch (error) {
    console.error('更新会话置顶状态失败：', error)
  } finally {
    actionBusy.value = false
  }
}

const confirmDialog = async (value) => {
  if (actionBusy.value || !dialogSession.value) return
  const title = value.trim()
  if (dialogType.value === 'rename' && !title) return
  const session = dialogSession.value
  actionBusy.value = true
  try {
    if (dialogType.value === 'rename') {
      await renameSession({ id: session.sessionId, request: { title } })
      if (appStore.aiSalesMentorSession?.sessionId === session.sessionId) {
        appStore.setAiSalesMentorSession({ ...appStore.aiSalesMentorSession, title })
      }
    } else {
      await deleteSession({ id: session.sessionId })
      await waitForPendingRequests()
      removeLocalSession(session.sessionId)
      if (appStore.aiSalesMentorSession?.sessionId === session.sessionId) {
        appStore.setOpenAiSessionId('')
        appStore.setAiSalesMentorSession(null)
        emitter.emit('newAiSalesMentorConversation')
      }
    }
    dialogVisible.value = false
    if (dialogType.value === 'rename') await refreshFirstPageSessions()
  } catch (error) {
    console.error('更新会话失败：', error)
  } finally {
    actionBusy.value = false
  }
}

const handleClickSession = (session) => {
  emits('selected')
  if (appStore.aiSalesMentorSession?.sessionId === session.sessionId) return

  if (session.isRead === 0) {
    updateSession({
      id: session.sessionId,
      isRead: 1
    }).finally(() => {
      refreshFirstPageSessions()
    })
  }
  appStore.setAiSalesMentorSession(session)
  appStore.setOpenAiSessionId(session.sessionId)

  if (route.name === (props.mobile ? 'qcaChat' : 'aiAgent')) {
    emitter.emit('openSession', session.sessionId)
  } else {
    emits('handleOpenAiAgent')
  }
}

const handlePageActive = () => {
  if (document.visibilityState !== 'visible') return
  // 合并切回页面时相邻触发的 visibilitychange 和 focus 事件。
  clearTimeout(pageActiveTimer)
  pageActiveTimer = setTimeout(() => {
    if (document.visibilityState === 'visible') refreshFirstPageSessions()
  }, 100)
}

onMounted(() => {
  if (!props.mobile) {
    desktopEventsRegistered = true
    emitter.on('openAiAgentSession', handleClickSession)
    emitter.on('refreshAiAgentSessions', refreshFirstPageSessions)
    document.addEventListener('visibilitychange', handlePageActive)
    window.addEventListener('focus', handlePageActive)
  }
  loadSessions()
})
onBeforeUnmount(() => {
  isUnmounted = true
  if (desktopEventsRegistered) {
    emitter.off('openAiAgentSession', handleClickSession)
    emitter.off('refreshAiAgentSessions', refreshFirstPageSessions)
    document.removeEventListener('visibilitychange', handlePageActive)
    window.removeEventListener('focus', handlePageActive)
    clearTimeout(pageActiveTimer)
  }
})

defineExpose({
  loadMore: handleLoadMore,
  refresh: refreshFirstPageSessions,
  sessionsList: recentSessions,
  canLoadMore: computed(
    () => expanded.value && !actionBusy.value && !sessionsLoading.value && !sessionsFinished.value
  )
})
</script>

<style lang="scss" scoped>
.ai-menu-v2__recent {
  padding-top: 4px;
  margin-top: 12px;
  user-select: none;
  .is-mobile {
    height: 36px;
  }
  .ai-menu-v2__recent-title {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding: 6px 12px;
    border: 0;
    background: transparent;
    color: #858e9f;
    font: inherit;
    font-size: 12px;
    line-height: 18px;
    cursor: pointer;
    .t-iconfont {
      margin-left: 4px;
      font-size: 16px;
      line-height: 16px;
    }
    &:focus-visible {
      outline: 2px solid #7a52f4;
      outline-offset: -2px;
    }
  }
}
.ai-menu-v2__recent-list {
  margin-top: 2px;
}
.ai-menu-v2__session-status {
  display: block;
  width: 100%;
  padding: 8px 12px;
  box-sizing: border-box;
  text-align: center;
  color: #858e9f;
  font-size: 12px;
}
</style>
